/**
 * 마스터 자격 박탈 — 목록(EA-18)·강등 요청(EA-19)·상세(EA-20)·승인(EA-21)·거부(EA-22).
 *
 * 계약: XM-38(요청 — 회원 **번호**)·XM-39(목록, offset)·XM-40(상세 + 정산 예상)·XM-41·XM-42.
 *
 * ⚠ **버튼은 역할이 아니라 키로 그린다.** 요청은 `revocations.request` + 아이디로 번호를
 * 찾는 `members.view`(명세 0-5, 7절 C-06), 결정은 `revocations.decide` 하나.
 *
 * ⚠ **화면이 막는 것은 친절이고, 막는 것은 서버다.** 중복 요청도 자기 승인도 서버가
 * 최종적으로 거절한다(403 `self_approval`).
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import {
  approveRevocation, getRevocation, listRevocations, rejectRevocation, requestRevocation,
} from '@/api/admin'
import { findMemberId } from '@/api/lookup'
import { ApiError, errorMessage } from '@/api/errors'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import {
  AdminRef, Button, Card, Field, Labeled, LINK, Maybe, MemberLink, Notice, Row, TD, TextArea, TH,
  Time,
} from '@/components/ui'
import { Confirm, sendWrite, submitWrite, useWrite } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { OffsetPager } from '@/components/Pager'
import { Choice, FilterBar, optionsOf, useFilters } from '@/components/Filters'
import { SettlementTable } from './Collateral'
import { GRADE_LABEL, REVOCATION_STATUS_LABEL, type RevocationDetail } from '@/api/types'

const LIMIT = 25
const FILTERS = ['status'] as const

/* ── EA-18 목록 (XM-39) ──────────────────────────────────────────── */

export function RevocationsPage() {
  const f = useFilters(FILTERS)
  const params = { status: f.values['status'] || undefined, offset: f.offset, limit: LIMIT }
  const q = useQuery({ queryKey: ['revocations', params], queryFn: () => listRevocations(params) })

  return (
    <Card title="Master revocations">
      <div className="mb-4">
        <RequestRevocationButton />
      </div>
      {/* 대상 아이디 거르개는 계약에 없다(7절 C-07). */}
      <FilterBar active={f.active} onClear={f.clear}>
        <Choice label="Status" value={f.values['status'] ?? ''}
          options={optionsOf(REVOCATION_STATUS_LABEL)} onChange={(v) => f.set('status', v)} />
      </FilterBar>

      <QueryState isPending={q.isPending} error={q.error} empty={q.data?.length === 0}
        emptyText="No requests match the filters.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Target</th><th className={TH}>Status</th>
            <th className={TH}>Reason</th><th className={TH}>Requested by</th><th className={TH}>Decided by</th>
            <th className={TH}>Demoted members</th>
          </tr></thead>
          <tbody>
            {(q.data ?? []).map((r) => (
              <tr key={r.revocationId}>
                <td className={TD}>
                  <Link className={LINK} to={`/master-revocations/${r.revocationId}`}>{r.revocationId}</Link>
                </td>
                <td className={TD}><MemberLink memberId={r.memberId} loginId={r.loginId} /></td>
                <td className={TD}><Labeled labels={REVOCATION_STATUS_LABEL} code={r.status} /></td>
                <td className={TD}>{r.reason}</td>
                <td className={TD}><AdminRef adminId={r.requestedByAdminId} /></td>
                <td className={TD}><AdminRef adminId={r.decidedByAdminId} /></td>
                {/* ⚠ 결정 전에는 null — 0명이 아니라 「아직」이다. */}
                <td className={TD}><Maybe value={r.demotedCount} unit=" members" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      <OffsetPager offset={f.offset} limit={LIMIT} count={q.data?.length} onChange={f.setOffset} />
    </Card>
  )
}

/* ── EA-19 MASTER 강등 요청 창 (XM-23 → XM-38) ───────────────────── */

export function RequestRevocationButton() {
  const { has } = useSession()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [member, setMember] = useState('')
  const [reason, setReason] = useState('')
  const [risk, setRisk] = useState('')
  const [withdrawalWaits, setWithdrawalWaits] = useState(false)
  const w = useWrite()

  // 요청 키 `revocations.request` + 아이디로 회원 번호를 찾는 `members.view`(명세 0-5, 7절 C-06).
  if (!has('revocations.request', 'members.view')) return null

  function close() {
    setOpen(false); setMember(''); setReason(''); setRisk(''); setWithdrawalWaits(false); w.setError(null)
  }

  async function submit() {
    const loginId = member.trim()
    setWithdrawalWaits(false)
    await w.run(async () => {
      let memberId: number | null
      try {
        memberId = await findMemberId(loginId)
      } catch (err) {
        w.setError(errorMessage(err)); return
      }
      if (memberId === null) { w.setError('There is no such member.'); return }
      const id = memberId
      const send = async () => {
        await requestRevocation({
          memberId: id, reason: reason.trim(), riskReason: risk.trim() || undefined,
        })
        await qc.invalidateQueries({ queryKey: ['revocations'] })
        setFlash({ tone: 'ok', text: `Submitted a demotion request for ${loginId}. It waits for a super admin's approval.` })
      }
      await sendWrite(w, 'Demotion request', send, close, { onError: (err) => setWithdrawalWaits(withdrawalPending(err)) })
    })
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}>Request MASTER demotion</Button>
      <Confirm
        open={open} title="Request MASTER demotion" action="Submit request"
        busy={w.busy} error={w.error} onCancel={close} onConfirm={() => { void submit() }}
        disabled={member.trim() === '' || reason.trim() === ''}
      >
        <div className="flex flex-col gap-3">
          <p>
            A request alone <strong>changes nothing.</strong> The grade changes only when a super admin
            approves it.
          </p>
          <Field label="Target member ID" value={member} onChange={(e) => setMember(e.target.value)} />
          <Field label="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Field label="Risk reason (optional)" value={risk} onChange={(e) => setRisk(e.target.value)} />
          {withdrawalWaits && <PendingWithdrawalsLink />}
        </div>
      </Confirm>
    </>
  )
}

/* ── EA-20 상세 (XM-40) ──────────────────────────────────────────── */

export function RevocationDetailPage() {
  const { id = '' } = useParams()
  const revocationId = Number(id)
  const q = useQuery({ queryKey: ['revocation', revocationId], queryFn: () => getRevocation(revocationId) })
  const r = q.data

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/master-revocations" className="text-xs text-slate-500 underline">← Master revocations</Link>
      </div>
      <Card title={`Master revocation #${id}`}>
        <QueryState isPending={q.isPending} error={q.error}>
          {r && (
            <>
              <dl className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
                <Row label="Target"><MemberLink memberId={r.memberId} loginId={r.loginId} /></Row>
                <Row label="Current grade"><Labeled labels={GRADE_LABEL} code={r.grade} /></Row>
                <Row label="Status"><Labeled labels={REVOCATION_STATUS_LABEL} code={r.status} /></Row>
                <Row label="Reason">{r.reason}</Row>
                <Row label="Risk reason"><Maybe value={r.riskReason} /></Row>
                <Row label="Related incident">
                  {r.relatedIncidentId === null
                    ? <Maybe value={null} />
                    : <Link className={LINK} to={`/incidents/${r.relatedIncidentId}`}>#{r.relatedIncidentId}</Link>}
                </Row>
                <Row label="Related trade"><Maybe value={r.relatedOrderId} /></Row>
                <Row label="Requested by"><AdminRef adminId={r.requestedByAdminId} /></Row>
                <Row label="Requested at"><Time ms={r.requestedAt} /></Row>
                <Row label="Decided by"><AdminRef adminId={r.decidedByAdminId} /></Row>
                <Row label="Decided at"><Time ms={r.decidedAt} /></Row>
                <Row label="Denial reason"><Maybe value={r.rejectReason} /></Row>
                <Row label="Demoted members"><Maybe value={r.demotedCount} unit=" members" /></Row>
              </dl>

              {/* 계약은 대기일 때만 명단을 준다 — 대기가 아니면 강등 수만(위 칸). */}
              {r.status === 'REQUESTED' && (
                <>
                  <h3 className="mb-2 mt-5 text-sm font-semibold">
                    Members under the master who also become general members ({r.specials?.length ?? 0})
                  </h3>
                  {(r.specials ?? []).length === 0
                    ? <p className="text-sm text-slate-500">There are no such members.</p>
                    : (
                      <ul className="list-disc pl-5 text-sm">
                        {(r.specials ?? []).map((s) => (
                          <li key={s.memberId}><MemberLink memberId={s.memberId} loginId={s.loginId} /></li>
                        ))}
                      </ul>
                    )}
                </>
              )}

              <DecideRevocation detail={r} />
            </>
          )}
        </QueryState>
      </Card>
    </div>
  )
}

/* ── EA-21 승인 · EA-22 거부 (XM-41 · XM-42) ─────────────────────── */

export function DecideRevocation({ detail }: { detail: RevocationDetail }) {
  const { has, me } = useSession()
  const qc = useQueryClient()
  const [mode, setMode] = useState<'approve' | 'reject' | null>(null)
  const [reason, setReason] = useState('')
  const [withdrawalWaits, setWithdrawalWaits] = useState(false)
  const w = useWrite()

  // 결정 키 `revocations.decide` 하나(명세 0-5 — 레거시는 `ADMIN_MANAGE` 도 요구했다).
  if (!has('revocations.decide')) return null
  // 이미 결정된 요청에는 단추가 없다 — 서버도 `already_reviewed` 로 막는다.
  // 발견-627: 창이 열린 채 409 로 다시 읽어 결정됨이 되면 창(빨간 글)은 남기고 승인만 잠근다 — 닫으면 사라진다.
  const decided = detail.status !== 'REQUESTED'
  if (decided && mode === null) return null

  // ⚠ **자기 요청은 자기가 승인 못 한다.** 화면에서도 보여 주지만, 막는 것은 서버다
  // (403 `self_approval`).
  const mine = me !== null && me.adminId === detail.requestedByAdminId
  const specials = detail.specials ?? []

  function close() { setMode(null); setReason(''); setWithdrawalWaits(false); w.setError(null) }

  async function submit() {
    const approve = mode === 'approve'
    const why = reason.trim()
    setWithdrawalWaits(false)
    const send = async () => {
      const r = approve
        ? await approveRevocation(detail.revocationId)
        : await rejectRevocation(detail.revocationId, why)
      // ⚠ 낡은 자료를 남기지 않는다 — 목록·상세·회원·등급 이력이 다 바뀐다.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['revocations'] }),
        qc.invalidateQueries({ queryKey: ['revocation', detail.revocationId] }),
        qc.invalidateQueries({ queryKey: ['member', detail.memberId] }),
        qc.invalidateQueries({ queryKey: ['grade-history', detail.memberId] }),
        qc.invalidateQueries({ queryKey: ['members'] }),
        qc.invalidateQueries({ queryKey: ['partners'] }),
      ])
      setFlash({
        tone: 'ok',
        text: approve
          ? `Revoked the master status of ${r.loginId}. ${r.demotedCount ?? 0} members under them also became general members.`
          : `Denied the demotion request for ${r.loginId}. The grade stays the same.`,
      })
    }
    await submitWrite(w, approve ? 'Approve demotion' : 'Deny demotion', send, close,
      {
        onError: (err) => {
          setWithdrawalWaits(withdrawalPending(err))
          // 발견-627: 409(already_reviewed — 다른 사람이 먼저 결정 등)면 화면이 낡았다 — 상세와 목록을 다시 읽는다.
          if (err instanceof ApiError && err.status === 409) {
            void qc.invalidateQueries({ queryKey: ['revocation', detail.revocationId] })
            void qc.invalidateQueries({ queryKey: ['revocations'] })
          }
        },
      })
  }

  return (
    <div className="mt-5 border-t border-slate-200 pt-4 dark:border-slate-700">
      {decided ? null : mine ? (
        <Notice tone="warn">
          <strong>You cannot approve a request you made yourself.</strong> Another super admin must
          decide.
        </Notice>
      ) : (
        <div className="flex gap-2">
          <Button onClick={() => {
            // ⚠ 정산 예상은 창을 열 때 새로 읽는다 — 10초 전 잔액으로 돈을 판단하지 않게.
            void qc.invalidateQueries({ queryKey: ['revocation', detail.revocationId] })
            setMode('approve')
          }}>Approve</Button>
          <Button tone="plain" onClick={() => setMode('reject')}>Deny</Button>
        </div>
      )}

      <Confirm
        open={mode !== null}
        title={mode === 'approve' ? 'Approve master revocation' : 'Deny demotion request'}
        action={mode === 'approve' ? 'Approve' : 'Deny'}
        danger={mode === 'approve'}
        busy={w.busy} error={w.error} onCancel={close} onConfirm={() => { void submit() }}
        disabled={decided || (mode === 'reject' && reason.trim() === '')}
      >
        <div className="flex flex-col gap-3">
          {mode === 'approve' ? (
            <>
              <p><strong>{detail.loginId}</strong> becomes a general member.</p>
              <p>
                <strong>{specials.length}</strong> members under them also become general members.
                {specials.length > 0 && (
                  <span className="ml-1 text-slate-500">
                    ({specials.map((s) => s.loginId).join(', ')})
                  </span>
                )}
              </p>
              {/*
                ⚠ **돈** — 승인 전에 정산 예상 네 값을 보인다(계약 XM-40 비고, 고침 3회차).
                예상이다: 승인은 차감을 멈추고 체인에서 끝낸 뒤 그때 잔액으로 다시 센다.
              */}
              {/* 발견-629: 이미 결정된 건은 다시 계산하지 않는다 — 체인을 못 읽었다는 안내를 띄우지 않는다. */}
              {detail.settles === true && !(decided && !detail.recomputed) && (
                detail.recomputed
                  ? <SettlementTable columns={[{ label: 'Expected settlement (current balance)', values: detail.recomputed }]} />
                  : <Notice tone="warn">The current balance could not be read from the chain, so there are no expected settlement values.</Notice>
              )}
              {/*
                정할 것 56 (사장님 2026-10-13 「3」·2026-10-14 「ㅇ」 — 박탈도 철회와 같은 정산): 철회 승인 창(Collateral.tsx)과 같은 문장.
                'Refund' 는 정산 예상 표(Refund 줄)가 보일 때만 말한다 — 서버의 Refund 는 마스터 지갑에 닿는 금액(거래소 서버 242f5a5).
              */}
              <p className="text-xs text-slate-500">
                Approval is execution. The grade change, the history and the audit record are processed together,
                and if any of them fails, everything is rolled back. The collateral is settled like a withdrawal —
                the penalty first, then the remaining fees, and the rest to the master. The transfer fee (0.17%) and
                gas (0.1%) are taken inside each amount the contract sends out
                {detail.settles === true && detail.recomputed
                  ? ", so the Refund shown is what reaches the master's wallet."
                  : '.'}
              </p>
              {withdrawalWaits && <PendingWithdrawalsLink />}
            </>
          ) : (
            <>
              <p>Closes this request. <strong>The grade does not change.</strong></p>
              <TextArea label="Denial reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
            </>
          )}
        </div>
      </Confirm>
    </div>
  )
}

/**
 * 사장님 2026-10-15 「철회신청이되있는데 굳이 관리자가 철회 신청중인걸 알면 박탈할필요가없잖아 / 차라리 철회 승인이 되는게 맞지」: 서버가
 * 박탈 요청·승인(XM-38·XM-41)을 409 `withdrawal_request_pending` 으로 막으면 — 그 마스터의 철회 신청이 대기 중 — 창 안 글(빨간
 * 안내)에 더해, 철회 키가 있으면 대기 철회 목록(있는 화면 — 보증금 › 철회 탭, EA-16)으로 가는 길. 철회 승인은 관리자가 한다.
 */
function withdrawalPending(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'withdrawal_request_pending'
}

function PendingWithdrawalsLink() {
  const { has } = useSession()
  if (!has('withdrawals.view')) return null
  return (
    <p className="text-sm">
      <Link className={LINK} to="/collateral?tab=withdrawals&withdrawalStatus=PENDING">Open the pending withdrawal requests</Link>
    </p>
  )
}
